<template>
    <div class="ask-pendo-workflow">
        <div class="ask-pendo-workflow__header">
            <div class="ask-pendo-workflow__header-content">
                <div class="ask-pendo-workflow__title">
                    {{ title }}
                </div>
                <div class="ask-pendo-workflow__subtitle">
                    {{ subtitle }}
                </div>
            </div>
            <div class="ask-pendo-workflow__actions">
                <pendo-button
                    v-if="allTodosCompleted"
                    type="subtle"
                    size="mini"
                    suffix-icon="x"
                    @click="handleDismiss">
                    {{ $t('askPendo.Dismiss plan') }}
                </pendo-button>
                <pendo-button
                    class="ask-pendo-workflow__toggle"
                    :class="{ 'ask-pendo-workflow__toggle--collapsed': isCollapsed }"
                    type="subtle"
                    size="mini"
                    suffix-icon="chevron-down"
                    @click="isCollapsed = !isCollapsed">
                    {{ isCollapsed ? $t('askPendo.Show') : $t('askPendo.Hide') }}
                </pendo-button>
            </div>
        </div>
        <div
            class="ask-pendo-workflow__body"
            :class="{ 'ask-pendo-workflow__body--collapsed': isCollapsed }">
            <div class="ask-pendo-workflow__body-inner">
                <todo-list-artifact :data="todos" />
            </div>
        </div>
    </div>
</template>

<script>
import { PendoButton } from '@pendo/components';
import TodoListArtifact from '../artifacts/todo-list-artifact/todo-list-artifact.vue';

export default {
    components: {
        PendoButton,
        TodoListArtifact,
    },
    props: {
        todos: {
            type: Array,
            default: null,
        },
        title: {
            type: String,
            default: 'Todo List',
        },
    },
    emits: ['dismiss'],
    data () {
        return {
            isCollapsed: false,
        };
    },
    computed: {
        subtitle () {
            const completed = this.todos?.reduce((acc, todo) => {
                return todo.status === 'completed' ? acc + 1 : acc;
            }, 0);

            return `${completed} / ${this.todos?.length} completed`;
        },
        allTodosCompleted () {
            if (!this.todos || this.todos.length === 0) return false;

            return this.todos.every((todo) => todo.status === 'completed');
        },
    },
    watch: {
        allTodosCompleted: {
            immediate: true,
            handler (isComplete) {
                if (isComplete) {
                    this.isCollapsed = true;
                }
            },
        },
    },
    methods: {
        handleDismiss () {
            this.$emit('dismiss');
        },
    },
};
</script>

<style scoped lang="less">

.ask-pendo-workflow {
    background-color: var(--palette-gray-0);
    border: solid 1px;
    border-color: var(--palette-gray-40);
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom: none;
    padding: 16px 16px 0;
    width: 100%;

    &__header {
        margin-bottom: 16px;
        display: flex;
        justify-content: space-between;
    }

    &__title {
        font-size: 14px;
        font-weight: 600;
        color: var(--palette-teal-90)
    }

    &__subtitle {
        font-size: 12px;
        font-weight: 400;
        color: var(--palette-gray-70)
    }

    &__actions {
        display: grid;
        grid-template-columns: 1fr 0.65fr;
        align-items: center;
        gap: 8px;
    }

    &__toggle {
        grid-column: 2;
        justify-self: end;

        :deep(svg) {
            transition: transform 0.3s ease;
        }

        &--collapsed {
            :deep(svg) {
                transform: rotate(180deg);
            }
        }
    }

    &::after {
        content: '';
        position: absolute;
        left: 16px;
        right: 16px;
        height: 10px;
        border-left: solid 1px var(--palette-gray-40);
        border-right: solid 1px var(--palette-gray-40);
        background-color: var(--palette-gray-0);
    }

    &__body {
        display: grid;
        grid-template-rows: 1fr;
        margin-bottom: 16px;
        transition: grid-template-rows 0.3s ease, margin 0.3s ease;

        &--collapsed {
            grid-template-rows: 0fr;
            margin-bottom: 0;
        }
    }

    &__body-inner {
        overflow: hidden;
    }
}
</style>
